import Vue from 'vue'
import Vuex from 'vuex'
Vue.use(Vuex)

import cookies from '../../plugins/cookies'
import state from './state'
import getters from './getters'
import mutations from './mutations'
import actions from './actions'

// Register any store modules that are required to start the app
// Dynamically loaded modules should be loaded in their root components
const store = new Vuex.Store({
    getters,
    mutations,
    state,
    actions,
    modules: [],
    plugins: [],
    strict: process.env.NODE_ENV !== 'production'
})

const apply_reductions = (reducer, filters, mutation, state) => {
    if (filters.includes(mutation)) {
        return false
    }
    if (!reducer) {
        return state
    }
    var reduced_state = reducer(mutation, state)

    if (reduced_state !== false && typeof reduced_state !== 'object') {
        throw `reducer return type ${typeof reduced}. Must be a bool or object`
    }

    return reduced_state
}

store.unregister = (sub_module) => {
    if (typeof sub_module === 'object' && sub_module.name) {
        sub_module = sub_module.name
    }
    store.unregisterModule(sub_module)
}

store.register = ({
    name,
    getters,
    mutations,
    actions,
    state,
    modules,
    require_auth,
    persistence_options,
    persistence,
    watchers
}) => {
    if (require_auth) {
        // These modules require authentication before they can exist. Primarily only
        // used for application wide stores with this requirement (staff details etc)
        // authenticated modules will only initialize while authenticated and will clear
        // their state on loss of auth.
        store.watch(
            (state, getters) => getters['auth/is_authenticated'],
            async function (newv) {
                if (newv === undefined) {
                    throw 'Auth module not loaded when loading authenticated submodule, have you specified the correct order?'
                }
                if (newv) {
                    // we are authenticated
                    if (!store.hasModule(name)) {
                        await store.register({
                            name,
                            getters,
                            mutations,
                            actions,
                            state,
                            modules,
                            require_auth: false, // This prevents infinite loop
                            persistence_options,
                            persistence,
                            watchers
                        })
                    }
                } else {
                    // losing authentication
                    if (store.hasModule(name)) {
                        setTimeout(function () {
                            store.commit(`${name}/CLEAR`)
                            store.unregister(name)
                        }, 250)
                    }
                }
            },
            {
                immediate: true
            }
        )
        // break the standard register process, it has been deferred
        return
    }

    var load = () => {}
    var save = () => {}
    var subscribeMutations = () => {}
    var reducer = null
    var filters = ['_INITIALIZE', 'CLEAR']
    var module_prefix = `${name}/`
    getters = getters == null ? {} : getters
    actions = actions == null ? {} : actions
    mutations = mutations == null ? {} : mutations
    modules = modules == null ? {} : modules

    if (persistence_options) {
        reducer = persistence_options.reducer
        filters = filters.concat(persistence_options.filters ? persistence_options.filters : [])
    }

    if (!name) {
        throw 'registered store modules MUST have a name'
    }

    if (state) {
        if (typeof state === 'object') {
            const default_state = state
            state = () => default_state
        } else if (typeof state !== 'function') {
            throw 'state must be a function which returns the default state structure or the default state object'
        }
    } else {
        state = () => {
            return {}
        }
    }

    var default_state = state()
    var saved_state = {}

    if (typeof default_state !== 'object') {
        throw 'state() must return a json object'
    }

    if (reducer) {
        if (typeof reducer !== 'function') {
            throw 'reducer must be a function which returns either a bool or a potentially reduced form of the state'
        }
    } else {
        reducer = (mutation, state) => {
            return state
        }
    }

    if (persistence) {
        if (persistence.load && typeof persistence.load !== 'function') {
            throw 'load() must return a json object'
        }

        if (typeof persistence === 'object') {
            if ('load' in persistence) {
                load = persistence['load']
            }
            if ('save' in persistence) {
                save = persistence['save']
            }
        } else {
            if (persistence === 'localStorage') {
                load = () => {
                    return JSON.parse(localStorage.getItem(name))
                }
                save = (mutation, state) => {
                    localStorage.setItem(name, JSON.stringify(state))
                }
            } else if (persistence === 'session') {
                load = () => {
                    return JSON.parse(sessionStorage.getItem(name))
                }
                save = (mutation, state) => {
                    sessionStorage.setItem(name, JSON.stringify(state))
                }
            } else if (persistence === 'cookie') {
                load = () => {
                    return JSON.parse(cookies.get(name))
                }
                save = (mutation, state) => {
                    cookies.set({
                        key: name,
                        value: JSON.stringify(state)
                    })
                }
            } else if (persistence === 'zutak') {
                load = async () => {
                    try {
                        await document.promisedEvent('zutakReady')
                        var data = await Zutak.storage.readFile(`${name}_vuex.json`)
                        return JSON.parse(data)
                    } catch (error) {
                        //file doesn't exist
                        return {}
                    }
                }
                save = async (mutation, state) => {
                    await Zutak.storage.saveFile(`${name}_vuex.json`, state, true)
                }
            } else if (typeof persistence === 'string') {
                throw `persistence strategy: ${persistence}, not supported. Current supported: [cookie, localStorage]`
            }
        }
        // todo: loading needs to merge with current state otherwise new modifications to the structure will be overridden by the saved state object
        saved_state = load()

        subscribeMutations = () => {
            store.subscribe((mutation, state) => {
                if (mutation.type.startsWith(module_prefix)) {
                    var module_mutation = mutation.type.replace(module_prefix, '')
                    var reduced_state = apply_reductions(
                        reducer,
                        filters,
                        module_mutation,
                        state[name]
                    )
                    if (reduced_state) {
                        save(module_mutation, reduced_state)
                    }
                }
            })
        }
    }

    const new_mutations = {
        CLEAR: (vuex_state) => {
            Object.assign(vuex_state, state())
        },
        _INITIALIZE(state, new_state) {
            Object.assign(state, new_state)
        },
        ...mutations
    }

    var new_state = {
        ...default_state
    }

    if (saved_state) {
        if (!saved_state.then) {
            const injectSavedStateToSubModules = function (mods, parent_state) {
                if (!mods) {
                    return
                }
                Object.keys(mods).forEach((mod_name) => {
                    var mod = mods[mod_name]
                    var new_mod_state = mod.state()
                    var saved_mod_state = parent_state[mod_name]
                    delete parent_state[mod_name]
                    mod.state = () => Object.assign({}, new_mod_state, saved_mod_state)
                    injectSavedStateToSubModules(mod.modules, saved_mod_state)
                })
            }

            // load is async promise
            Object.assign(new_state, saved_state)
            injectSavedStateToSubModules(modules, new_state)
        } else {
            var initializer_load = async (state, commit) => {
                commit(`${name}/_INITIALIZE`, await saved_state)
            }
        }
    }

    const new_module = {
        namespaced: true,
        state: new_state,
        mutations: new_mutations,
        getters,
        actions,
        modules
    }

    store.registerModule(name, new_module)

    if (watchers) {
        Object.keys(watchers).forEach((prop) => {
            if (!watchers[prop].handler || typeof watchers[prop].handler !== 'function') {
                throw `${prop} watcher needs a defined handler function.`
            }
            var attrs = { ...watchers[prop] }
            var accessor = (state, getters) => getters[prop]
            delete attrs.handler

            const handler = (current, old) => {
                watchers[prop].handler(store, current, old)
            }
            store.watch(accessor, handler, attrs)
        })
    }

    var init = null

    if (initializer_load) {
        init = initializer_load(new_module.state, store.commit)
    }

    if (new_module.actions.initialize) {
        if (init) {
            if (init.then) {
                init = init
                    .then(() => store.dispatch(`${name}/initialize`))
                    .then(() => subscribeMutations())
            }
        } else {
            init = store.dispatch(`${name}/initialize`)
            subscribeMutations()
        }
    }
    if (init && init.then) {
        return init
    }
    return Promise.resolve()
}

export default store
